import { useMemo, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { customerService } from '../services/customerService';

type ViewType = 'customer' | 'warehouse' | 'loading';

const VIEW_OPTIONS: Array<{ value: ViewType; label: string; viewName: string }> = [
  { value: 'customer', label: 'Müşteri Bilgileri', viewName: 'CUSTOMVIEW_ENTSBDOGUSMUSTERIGENEL' },
  { value: 'warehouse', label: 'Depo Bilgileri', viewName: 'CUSTOMVIEW_ENTSBDOGUSDEPOGENEL' },
  { value: 'loading', label: 'Yükleme Bilgileri', viewName: 'CUSTOMVIEW_ENTSBDOGUSYUKLEMEGENEL' },
];

export default function TestStokbarPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewType, setViewType] = useState<ViewType>('customer');
  const [kriter, setKriter] = useState('');
  const [result, setResult] = useState<any | null>(null);
  const [rawXml, setRawXml] = useState('');
  const [rawErrorXml, setRawErrorXml] = useState('');

  const selectedView = VIEW_OPTIONS.find((item) => item.value === viewType) || VIEW_OPTIONS[0];
  const tableColumns = useMemo(() => {
    const firstRow = result?.rows?.[0];
    return firstRow ? Object.keys(firstRow) : [];
  }, [result]);

  const handleFetch = async () => {
    try {
      setLoading(true);
      setError('');
      setResult(null);
      setRawXml('');
      setRawErrorXml('');

      const response: any = await customerService.getStokbarView({
        viewType,
        kriter: kriter.trim() || undefined,
      });

      if (!response?.success) {
        const rawDetail = response?.rawXml || response?.detail || response?.stack || '';
        if (rawDetail) setRawErrorXml(String(rawDetail));
        throw new Error(response?.message || 'Stokbar verisi alınamadı');
      }

      setResult(response.data);
      setRawXml(response.data?.xml || '');
      toast.success(`${response.data?.count || 0} kayıt alındı`);
    } catch (err: any) {
      const msg = err?.message || 'Bir hata oluştu';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Stokbar View Test</h1>
            <p className="mt-1 text-sm text-slate-500">View seçip kriter gönderin, parse edilmiş satırları ve ham XML çıktısını görün.</p>
          </div>
          <button
            type="button"
            onClick={handleFetch}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sorgula
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">View</label>
              <select
                value={viewType}
                onChange={(e) => setViewType(e.target.value as ViewType)}
                className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400"
              >
                {VIEW_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="mt-2 text-xs text-slate-500">View adı: <code>{selectedView.viewName}</code></div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Kriter</label>
              <input
                value={kriter}
                onChange={(e) => setKriter(e.target.value)}
                placeholder='Örn: ""YUKLEMEKODU = 382945""'
                className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400"
              />
              <div className="mt-2 text-xs text-slate-500">Kriter verilmeyecekse boş bırakın.</div>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {rawErrorXml && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 shadow-sm">
            <h2 className="mb-2 text-lg font-semibold text-red-900">Ham XML Yanıtı (Hata)</h2>
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-xl border border-red-300 bg-white p-4 text-xs text-red-800">{rawErrorXml}</pre>
          </div>
        )}

        {result && (
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Parse Edilmiş Kayıtlar</h2>
                  <p className="mt-1 text-sm text-slate-500">{result.viewName} • {result.count} kayıt</p>
                </div>
              </div>

              {result.rows?.length > 0 ? (
                <div className="overflow-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        {tableColumns.map((column) => (
                          <th key={column} className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-left font-semibold text-slate-700">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row: Record<string, string>, index: number) => (
                        <tr key={index} className="border-b border-slate-100 last:border-b-0">
                          {tableColumns.map((column) => (
                            <td key={column} className="align-top px-4 py-3 text-slate-700">
                              <div className="max-w-[260px] break-words">{row[column] || '-'}</div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">Kayıt bulunamadı.</div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Ham XML</h2>
              <p className="mt-1 text-sm text-slate-500">Servisten dönen orijinal yanıt</p>
              <pre className="mt-4 max-h-[70vh] overflow-auto whitespace-pre-wrap break-words rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">{rawXml || '-'}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
